import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { adminCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { TabBar, TabPanel } from '@/ui/TabBar/TabBar'
import { adminApi } from '../api/workspaces'
import { UsagePanel } from '../components/UsagePanel'
import { WorkspacePicker } from '../components/WorkspacePicker'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useTabParam } from '../hooks/useTabParam'
import { useAuthStore } from '../store/auth'
import { useWorkspaceStore } from '../store/workspace'
import type { WorkspaceInfo } from '../types/api'
import { resolveAdminWorkspaceId } from '../utils/adminWorkspace'
import { validateReturnTo } from '../utils/returnTo'
import styles from './AdminPage.module.css'
import { AgentsPanel } from './admin/AgentsPanel'
import { LogsTab } from './admin/LogsTab'
import { ProvidersTab } from './admin/ProvidersTab'
import { RunsTab } from './admin/RunsTab'
import { SystemTab } from './admin/SystemTab'
import { UsersTab } from './admin/UsersTab'
import { WorkspacesTab } from './admin/WorkspacesTab'

type Tab =
  | 'users'
  | 'workspaces'
  | 'providers'
  | 'agents'
  | 'usage'
  | 'runs'
  | 'logs'
  | 'system'

const TABS: readonly Tab[] = [
  'users',
  'workspaces',
  'providers',
  'agents',
  'usage',
  'runs',
  'logs',
  'system',
]

export function AdminPage() {
  // The active tab lives in ?tab= so pages opened from a tab (a run trace) can
  // send the user back to the tab they left, not to the default one.
  const [tab, setTab] = useTabParam(TABS, 'users')
  const [searchParams, setSearchParams] = useSearchParams()

  const mainWorkspaceId = useWorkspaceStore((state) => state.activeWorkspaceId)
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin)
  const [creatingProvider, setCreatingProvider] = useState(false)
  const workspacesQuery = useQuery<WorkspaceInfo[]>({
    queryKey: ['admin', 'workspaces'],
    queryFn: adminApi.listAllWorkspaces,
  })
  const workspaces = workspacesQuery.data ?? []
  const showWorkspacesSkeleton = useSkeletonDelay(workspacesQuery.isPending)
  const workspacesLoaded = workspacesQuery.isSuccess
  const workspaceParam = searchParams.get('workspace_id')
  const activeWorkspaceId = resolveAdminWorkspaceId(
    workspaceParam,
    mainWorkspaceId,
    workspaces,
  )
  const selectedWorkspace = workspaces.find(
    (workspace) => workspace.workspace_id === activeWorkspaceId,
  )

  // Canonicalize fallbacks into the URL once the Admin workspace list is
  // authoritative. Back/Forward and hard refresh can then restore the exact
  // Admin-local selection without touching the main workspace store.
  useEffect(() => {
    if (!workspacesLoaded) return
    const currentTab = searchParams.get('tab')
    const currentWorkspace = searchParams.get('workspace_id')
    if (
      currentTab === tab &&
      currentWorkspace === (activeWorkspaceId || null)
    ) {
      return
    }
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous)
        params.set('tab', tab)
        if (activeWorkspaceId) params.set('workspace_id', activeWorkspaceId)
        else params.delete('workspace_id')
        return params
      },
      { replace: true },
    )
  }, [activeWorkspaceId, searchParams, setSearchParams, tab, workspacesLoaded])

  const setSelectedWorkspaceId = (workspaceId: string) =>
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous)
        if (workspaceId) params.set('workspace_id', workspaceId)
        else params.delete('workspace_id')
        return params
      },
      { replace: false },
    )

  // The agents, usage and runs tabs need the shared workspaces list: while it
  // loads they show a skeleton, and when it fails with nothing loaded the
  // panel's one Alert is the whole message (no "No workspaces" beneath it). It
  // sits in the panel, so the tab bar stays put across tabs.
  const needsWorkspaces = (content: ReactNode) =>
    workspacesQuery.isPending ? (
      <div aria-busy="true">
        {showWorkspacesSkeleton && <Skeleton lines={3} />}
      </div>
    ) : workspacesQuery.isError && workspacesQuery.data === undefined ? (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline" wrap>
          <Text>{adminCopy.loadFailed.workspaces}</Text>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void workspacesQuery.refetch()}
          >
            {adminCopy.retry}
          </Button>
        </Inline>
      </Alert>
    ) : (
      // The picker scopes the whole tab, so it is the tab's first row, above
      // the sections it scopes (Agents, Usage and Runs alike).
      <Stack gap="space-24" className={styles.workspaceTab}>
        <WorkspacePicker
          workspaces={workspaces}
          value={activeWorkspaceId}
          onChange={setSelectedWorkspaceId}
        />
        {content}
      </Stack>
    )
  const currentAdminHref =
    validateReturnTo(ROUTES.adminView(tab, activeWorkspaceId), {
      allowedKinds: ['admin'],
    }) ?? ROUTES.admin
  const workspaceActive =
    (selectedWorkspace?.lifecycle_status ?? 'active') === 'active'
  const canManageWorkspace = isSystemAdmin && workspaceActive

  return (
    <WsPageShell crumbs={[]} title={adminCopy.page.title}>
      <TabBar
        label={adminCopy.page.sections}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
        tabs={[
          { id: 'users', label: adminCopy.page.tabs.users },
          { id: 'workspaces', label: adminCopy.page.tabs.workspaces },
          { id: 'providers', label: adminCopy.page.tabs.providers },
          { id: 'agents', label: adminCopy.page.tabs.agents },
          { id: 'usage', label: adminCopy.page.tabs.usage },
          { id: 'runs', label: adminCopy.page.tabs.runs },
          { id: 'logs', label: adminCopy.page.tabs.logs },
          { id: 'system', label: adminCopy.page.tabs.system },
        ]}
      >
        <TabPanel id="users">
          <UsersTab />
        </TabPanel>
        <TabPanel id="workspaces">
          <WorkspacesTab />
        </TabPanel>
        <TabPanel id="providers">
          <ProvidersTab
            creating={creatingProvider}
            onCreatingChange={setCreatingProvider}
          />
        </TabPanel>
        <TabPanel id="agents">
          {needsWorkspaces(
            <AgentsPanel
              wsId={activeWorkspaceId}
              canManage={canManageWorkspace}
              returnTo={currentAdminHref}
            />,
          )}
        </TabPanel>
        <TabPanel id="usage">
          {needsWorkspaces(
            <UsagePanel
              wsId={activeWorkspaceId}
              canManage={canManageWorkspace}
            />,
          )}
        </TabPanel>
        <TabPanel id="runs">
          {needsWorkspaces(
            <RunsTab
              wsId={activeWorkspaceId}
              workspaces={workspaces}
              returnTo={currentAdminHref}
            />,
          )}
        </TabPanel>
        <TabPanel id="logs">
          <LogsTab />
        </TabPanel>
        <TabPanel id="system">
          <SystemTab />
        </TabPanel>
      </TabBar>
    </WsPageShell>
  )
}
